import CustomFieldValuesSelect from '../../custom-field-values-select';
import { concat, fn } from '@ember/helper';
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import eq from 'ember-truth-helpers/helpers/eq';
import _isNil from 'lodash/isNil';
import BvInline from 'volta/components/bv-inline';
import BvTag from 'volta/components/bv-tag';
import BvTextField from 'volta/components/bv-text-field';
import tElse from 'volta/helpers/t-else';
import { parseNumber } from 'volta/utils/math-utils';

import type { ICustomFieldDefinition } from 'volta/models/types/custom-field-definition';

export interface IInputFilterDefinitionArgs {
  definition?: ICustomFieldDefinition;
  inputType?: 'text' | 'richtext' | 'number' | 'date' | 'array';
}

export default class FiltersInputFilterComponent extends Component<
  FilterComponentSignature<IInputFilterDefinitionArgs>
> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    {{#if (eq @comparator 'bt')}}
      <BvInline
        @gap='1'
        @blockAlign='center'
        @align='space-between'
        @wrap={{false}}
        class='u-full-width'
      >
        <BvTextField
          @value={{this.leftValue}}
          @onChange={{this.valueLeftDidChange}}
          @name={{concat 'left-' @name}}
          @type={{this.inputType}}
          @max={{if (eq this.inputType 'number') this.max}}
          @error={{this.isFaulty}}
          @autoComplete='off'
          spellcheck='false'
          class='u-flex-fill'
        />
        <BvTag @size='sm' @text='~' class='u-flex-shrink-0 u-px-1' />
        <BvTextField
          @value={{this.rightValue}}
          @onChange={{this.valueRightDidChange}}
          @name={{concat 'right-' @name}}
          @min={{if (eq this.inputType 'number') this.min}}
          @error={{this.isFaulty}}
          @type={{this.inputType}}
          @autoComplete='off'
          spellcheck='false'
          class='u-flex-fill'
        />
      </BvInline>
    {{else}}
      {{#if this.showCustomFieldValueSelect}}
        <CustomFieldValuesSelect
          @customFieldDefinition={{@definition.customFieldDefinition}}
          @error={{@error}}
          @values={{this.values}}
          @onSave={{this.valuesDidChange}}
          @onChange={{this.valuesDidChange}}
          @label={{tElse @definition.name}}
          @triggerClass='u-full-width'
          @renderAll={{true}}
          @multiple={{true}}
          @onCreate={{null}}
        />
      {{else}}
        <BvTextField
          @value={{this.value}}
          @onChange={{this.valueDidChange}}
          @type={{this.inputType}}
          @autoComplete='off'
          @spellcheck='false'
          @allowClear={{true}}
          @onClearButtonClick={{fn this.valueDidChange undefined}}
          @name={{@name}}
          class='u-flex-fill'
        />
      {{/if}}
    {{/if}}
  </template>
  get min() {
    return parseNumber(this.leftValue);
  }

  get max() {
    return parseNumber(this.rightValue);
  }

  get values() {
    return this.args.values ?? [];
  }

  get inputType() {
    return this.args.componentArgs?.inputType ?? 'text';
  }

  get leftValue() {
    return this.value;
  }

  get value() {
    return this.values?.length ? this.values[0] : undefined;
  }

  get rightValue() {
    return this.values?.length === 2 ? this.values[1] : undefined;
  }

  get isFaulty() {
    if (_isNil(this.leftValue) || _isNil(this.rightValue) || this.inputType !== 'number') {
      return false;
    }
    return this.leftValue > this.rightValue;
  }

  get showCustomFieldValueSelect() {
    return (
      this.args.definition.customFieldDefinition &&
      (this.inputType === 'text' || this.inputType === 'array')
    );
  }

  @action
  valueDidChange(newValue?: string | number) {
    this.args.onChange?.(isPresent(newValue) ? [newValue as string] : []);
  }

  @action
  valuesDidChange(options: string[]) {
    this.args.onChange?.(options);
  }

  @action
  valueLeftDidChange(leftValue?: string | number) {
    const { values = [] } = this;
    if (isPresent(leftValue)) {
      values[0] = leftValue as string;
      if (values.length === 2) {
        this.args.onChange?.(values);
      }
    }
  }

  @action
  valueRightDidChange(rightValue?: string | number) {
    const { values = [] } = this;
    if (isPresent(rightValue)) {
      values[1] = rightValue as string;
      if (values.length === 2) {
        this.args.onChange?.(values);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::InputFilter': typeof FiltersInputFilterComponent;
    'filters/input-filter': typeof FiltersInputFilterComponent;
  }
}
